'use client';

import { c as _c } from "react/compiler-runtime";
import { decodeColor, formatCents } from 'betterplace-utils';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { CSSProperties, useMemo } from 'react';
import { composeSelectedFontObject } from '../helpers';
import StreambotWrapper from '../StreambotWrapper';
import type { StreambotComponentProps } from '../types';
import getDefaultValues from './getDefaultValues';
import { type StandardWidgetSettings } from './standardWidgetSettingsSchema';
import StyledAllInclusiveAlert from './StyledAllInclusiveAlert';
import Unstyled from './Unstyled';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import { Maybe } from '@/types';
type TotalData = {
  donated_amount_in_cents: number;
};
const totalDefaultData = {
  donated_amount_in_cents: 1337
} satisfies TotalData;
type DonationData = {
  donated_amount_in_cents?: number | undefined;
  author: Maybe<{
    name: Maybe<string>;
  }>;
};
const donationDefaultData = [{
  donated_amount_in_cents: 1337,
  author: {
    name: 'Unicorn&lt;3'
  }
}] satisfies DonationData[];
type StandardProps = StreambotComponentProps<Omit<StandardWidgetSettings, 'darkMode'>, undefined>;
function useData(fundraisingEventId, totalDemoData, donationDemoData) {
  const $ = _c(13);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(data) {
        if (!data) {
          return null;
        }
        return pick(data, ["donated_amount_in_cents"]);
      },
      intervalMs: 5000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const totalData = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}`, t0, t1, totalDemoData);
  let t2;
  if ($[3] !== locale) {
    t2 = {
      locale
    };
    $[3] = locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      selector(param) {
        const data_0 = param?.data;
        if (!data_0) {
          return null;
        }
        return data_0.map(_temp);
      },
      intervalMs: 5000
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const lastDonationData = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?per_page=1&page=1&facets=has_author_or_amount:true`, t2, t3, donationDemoData);
  let t4;
  if ($[6] !== locale) {
    t4 = {
      locale
    };
    $[6] = locale;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      selector(param_0) {
        const data_1 = param_0?.data;
        if (!data_1) {
          return null;
        }
        return data_1.map(_temp2);
      },
      intervalMs: 5000
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const topDonationData = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?order=amount_in_cents:DESC|id:ASC&per_page=1&page=1&facets=has_amount:true`, t4, t5, donationDemoData);
  const t6 = lastDonationData?.data?.[0];
  const t7 = topDonationData?.data?.[0];
  let t8;
  if ($[9] !== t6 || $[10] !== t7 || $[11] !== totalData) {
    t8 = {
      totalData,
      lastDonationData: t6,
      topDonationData: t7
    };
    $[9] = t6;
    $[10] = t7;
    $[11] = totalData;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function _temp2(opinion_0) {
  return pick(opinion_0, ["author", "donated_amount_in_cents"]);
}
function _temp(opinion) {
  return pick(opinion, ["author", "donated_amount_in_cents"]);
}
function transformPropKey(key: string) {
  if (!key) return key;
  const replaced = key.replace(/data|headline|screenName/gi, '');
  if (!replaced) return replaced;
  const init = replaced[0]!;
  const transformed = `${init.toLowerCase()}${replaced.slice(1)}`;
  switch (transformed) {
    case 'fontColor':
      return 'color';
    case 'font':
      return 'fontFamily';
  }
  return transformed;
}
function toCSSProperties(props: Record<string, string | number | undefined>) {
  return Object.fromEntries(Object.entries(props).map(([key, value_]) => {
    let value = value_;
    const transformed = transformPropKey(key);
    if (transformed === 'color') {
      value = decodeColor(value);
    }
    return [transformPropKey(key), value];
  })) as CSSProperties;
}
function Standard(t0) {
  const $ = _c(79);
  const {
    withDemoData: t1,
    ...props
  } = t0;
  const withDemoData = t1 === undefined ? false : t1;
  const t = useTranslations("nextjs.streambot");
  const locale = useLocale();
  const mergedProps = getDefaultValues(props);
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let displayMode;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let style;
  let wrapperProps;
  if ($[0] !== mergedProps) {
    ({
      displayMode,
      headlineFont,
      headlineFontWeight,
      headlineFontSize,
      headlineFontColor,
      dataFont,
      dataFontWeight,
      dataFontSize,
      dataFontColor,
      style,
      ...wrapperProps
    } = mergedProps);
    $[0] = mergedProps;
    $[1] = dataFont;
    $[2] = dataFontColor;
    $[3] = dataFontSize;
    $[4] = dataFontWeight;
    $[5] = displayMode;
    $[6] = headlineFont;
    $[7] = headlineFontColor;
    $[8] = headlineFontSize;
    $[9] = headlineFontWeight;
    $[10] = style;
    $[11] = wrapperProps;
  } else {
    dataFont = $[1];
    dataFontColor = $[2];
    dataFontSize = $[3];
    dataFontWeight = $[4];
    displayMode = $[5];
    headlineFont = $[6];
    headlineFontColor = $[7];
    headlineFontSize = $[8];
    headlineFontWeight = $[9];
    style = $[10];
    wrapperProps = $[11];
  }
  const isCustomized = displayMode === "custom";
  const widgetStyle = isCustomized ? undefined : style;
  const {
    totalData: t2,
    lastDonationData,
    topDonationData
  } = useData(mergedProps.fundraisingEventId, withDemoData ? totalDefaultData : undefined, withDemoData ? donationDefaultData : undefined);
  const {
    data: totalData
  } = t2;
  let t3;
  if ($[12] !== headlineFont || $[13] !== headlineFontColor || $[14] !== headlineFontSize || $[15] !== headlineFontWeight || $[16] !== isCustomized) {
    t3 = isCustomized ? {
      headlineFont,
      headlineFontColor,
      headlineFontSize,
      headlineFontWeight
    } : {
      headlineFont: "Fira Sans",
      headlineFontWeight: 700,
      headlineFontColor: "ffffff"
    };
    $[12] = headlineFont;
    $[13] = headlineFontColor;
    $[14] = headlineFontSize;
    $[15] = headlineFontWeight;
    $[16] = isCustomized;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const headlineProps = t3;
  let t4;
  if ($[18] !== dataFont || $[19] !== dataFontColor || $[20] !== dataFontSize || $[21] !== dataFontWeight || $[22] !== isCustomized) {
    t4 = isCustomized ? {
      screenNameFont: dataFont,
      screenNameFontColor: dataFontColor,
      screenNameFontSize: dataFontSize,
      screenNameFontWeight: dataFontWeight
    } : {
      screenNameFont: "Fira Sans",
      screenNameFontColor: "282828"
    };
    $[18] = dataFont;
    $[19] = dataFontColor;
    $[20] = dataFontSize;
    $[21] = dataFontWeight;
    $[22] = isCustomized;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const dataProps = t4;
  let t5;
  if ($[24] !== headlineProps.headlineFont || $[25] !== headlineProps.headlineFontWeight) {
    t5 = composeSelectedFontObject(headlineProps.headlineFont, headlineProps.headlineFontWeight);
    $[24] = headlineProps.headlineFont;
    $[25] = headlineProps.headlineFontWeight;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== dataProps.screenNameFont || $[28] !== dataProps.screenNameFontWeight) {
    t6 = composeSelectedFontObject(dataProps.screenNameFont, dataProps.screenNameFontWeight);
    $[27] = dataProps.screenNameFont;
    $[28] = dataProps.screenNameFontWeight;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== t5 || $[31] !== t6) {
    t7 = [t5, t6];
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const fonts = t7;
  let t8;
  if ($[33] !== fonts) {
    t8 = {
      fonts
    };
    $[33] = fonts;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  useGoogleFonts(t8);
  let t9;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      fontStyle: "italic"
    };
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const headlineFontStyle = t9;
  let t10;
  if ($[36] !== dataProps) {
    t10 = toCSSProperties(dataProps);
    $[36] = dataProps;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const dataStyle = t10;
  const headlineClassNames = `truncate-with-ellipsis column ${!isCustomized ? "gap" : ""}`;
  let t11;
  if ($[38] !== locale || $[39] !== t || $[40] !== totalData) {
    t11 = totalData?.donated_amount_in_cents ? formatCents(locale, totalData.donated_amount_in_cents) : t("placeholder");
    $[38] = locale;
    $[39] = t;
    $[40] = totalData;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const totalDonationAmount = t11;
  let t12;
  if ($[42] !== lastDonationData || $[43] !== locale || $[44] !== t) {
    t12 = lastDonationData?.donated_amount_in_cents ? formatCents(locale, lastDonationData.donated_amount_in_cents) : t("placeholder");
    $[42] = lastDonationData;
    $[43] = locale;
    $[44] = t;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const lastDonationAmount = t12;
  let t13;
  if ($[46] !== lastDonationData || $[47] !== t) {
    t13 = lastDonationData?.author?.name ? lastDonationData.author.name : t("screen_name.anonymous_user");
    $[46] = lastDonationData;
    $[47] = t;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const lastDonationDonor = t13;
  let t14;
  if ($[49] !== locale || $[50] !== t || $[51] !== topDonationData) {
    t14 = topDonationData?.donated_amount_in_cents ? formatCents(locale, topDonationData.donated_amount_in_cents) : t("placeholder");
    $[49] = locale;
    $[50] = t;
    $[51] = topDonationData;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const topDonationAmount = t14;
  let t15;
  if ($[53] !== t || $[54] !== topDonationData) {
    t15 = topDonationData?.author?.name ? topDonationData.author?.name : t("screen_name.anonymous_user");
    $[53] = t;
    $[54] = topDonationData;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  const topDonationDonor = t15;
  if (!widgetStyle && isCustomized) {
    let t16;
    if ($[56] !== dataProps || $[57] !== dataStyle || $[58] !== headlineClassNames || $[59] !== headlineProps || $[60] !== lastDonationAmount || $[61] !== lastDonationDonor || $[62] !== topDonationAmount || $[63] !== topDonationDonor || $[64] !== totalDonationAmount) {
      t16 = <Unstyled headlineClassNames={headlineClassNames} headlineFontStyle={headlineFontStyle} headlineProps={headlineProps} dataStyle={dataStyle} dataProps={dataProps} totalDonationAmount={totalDonationAmount} lastDonationAmount={lastDonationAmount} lastDonationDonor={lastDonationDonor} topDonationAmount={topDonationAmount} topDonationDonor={topDonationDonor} />;
      $[56] = dataProps;
      $[57] = dataStyle;
      $[58] = headlineClassNames;
      $[59] = headlineProps;
      $[60] = lastDonationAmount;
      $[61] = lastDonationDonor;
      $[62] = topDonationAmount;
      $[63] = topDonationDonor;
      $[64] = totalDonationAmount;
      $[65] = t16;
    } else {
      t16 = $[65];
    }
    let t17;
    if ($[66] !== t16 || $[67] !== wrapperProps) {
      t17 = <StreambotWrapper {...wrapperProps}>{t16}</StreambotWrapper>;
      $[66] = t16;
      $[67] = wrapperProps;
      $[68] = t17;
    } else {
      t17 = $[68];
    }
    return t17;
  }
  let t16;
  if ($[69] !== lastDonationAmount || $[70] !== lastDonationDonor || $[71] !== topDonationAmount || $[72] !== topDonationDonor || $[73] !== totalDonationAmount || $[74] !== widgetStyle) {
    t16 = <StyledAllInclusiveAlert totalDonationAmount={totalDonationAmount} lastDonationAmount={lastDonationAmount} lastDonationDonor={lastDonationDonor} topDonationAmount={topDonationAmount} topDonationDonor={topDonationDonor} kind={widgetStyle} />;
    $[69] = lastDonationAmount;
    $[70] = lastDonationDonor;
    $[71] = topDonationAmount;
    $[72] = topDonationDonor;
    $[73] = totalDonationAmount;
    $[74] = widgetStyle;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] !== t16 || $[77] !== wrapperProps) {
    t17 = <StreambotWrapper {...wrapperProps}>{t16}</StreambotWrapper>;
    $[76] = t16;
    $[77] = wrapperProps;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  return t17;
}
export default Standard;